// use-spotify-api.jsx — thin Spotify Web API client.
//
// Pairs with useSpotifyToken (which owns the bearer cache). This hook
// returns memoised callbacks for the endpoints the music card needs:
// search, recently-played, featured-playlists, top-tracks, plus the
// per-detail getters for S2 (album / playlist / artist / artist-top).
//
// Auth: every request resolves a fresh bearer via getToken(). On 401
// (bearer rejected — usually a Spotify-side revoke), force-refresh
// once and retry. Other non-2xx statuses surface as thrown Errors
// with the response body so the caller can render an error state.
//
// Spotify CDN URLs (i.scdn.co/image/...) are loaded directly as <img>
// src in the consuming components — no proxy involvement. Per
// BRIEF-music-card.md §11.5: Spotify CDN bypasses useImageBlobUrl.
// The proxy is reserved for entity_picture (Sonos signed URLs).

import * as React from 'react';
import { entryTrack } from '../music/spotify-shapes.js';

const { useCallback } = React;
const SPOTIFY_API = 'https://api.spotify.com/v1';

export function useSpotifyApi(getToken) {
  const request = useCallback(async (path, { params, ...init } = {}) => {
    const token = await getToken();
    // `path` is normally relative to SPOTIFY_API, but the pager below
    // passes Spotify's absolute `next` URLs straight back in — accept both.
    const url = new URL(path.startsWith('http') ? path : SPOTIFY_API + path);
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v != null) url.searchParams.set(k, v);
      }
    }
    const send = (bearer) => fetch(url, {
      ...init,
      headers: { Authorization: `Bearer ${bearer}`, ...(init.headers || {}) },
    });

    let res = await send(token);
    if (res.status === 401) {
      // Bearer rejected — force-refresh and retry exactly once.
      const fresh = await getToken({ force: true });
      res = await send(fresh);
    }
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`Spotify ${res.status} on ${path}${body ? `: ${body.slice(0, 200)}` : ''}`);
    }
    if (res.status === 204) return null;
    return res.json();
  }, [getToken]);

  // Page through an offset/cursor-paginated endpoint by following
  // Spotify's `next` links until exhausted, returning the flattened item
  // list. `node` selects the paging object from each response: identity
  // for /me/playlists & /me/albums (top-level { items, next }), and
  // r => r.artists for /me/following (cursor-paged, nested under
  // `artists`). MAX_PAGES is a runaway guard — 50/page × 10 = 500 items,
  // well above any real household library, and keeps us clear of the
  // Spotify rate-limit cascade (see spotify_rate_limit notes).
  const fetchAllPages = useCallback(async (firstPath, { params, node = (r) => r } = {}) => {
    const MAX_PAGES = 10;
    const items = [];
    let page = await request(firstPath, { params });
    for (let i = 0; i < MAX_PAGES && page; i++) {
      const pg = node(page) || {};
      if (Array.isArray(pg.items)) items.push(...pg.items);
      if (!pg.next) break;
      page = await request(pg.next);  // `next` is an absolute Spotify URL
    }
    return items;
  }, [request]);

  return {
    // /search rejected `limit=20` with "Invalid limit" on first try.
    // Spotify's docs say limit is integer 1..50 default 20, so 20
    // should be valid — but dropping the param and letting their
    // default kick in works. Adding `market: AU` to bias availability
    // for the household location. If this manifests again on other
    // endpoints, suspect URLSearchParams int-vs-string serialisation.
    search: (q, types = 'track,album,artist,playlist,episode,show', market = 'AU') =>
      request('/search', { params: { q, type: types, market } }),
    recentlyPlayed: (limit = 20) =>
      request('/me/player/recently-played', { params: { limit } }),
    featuredPlaylists: (limit = 20) =>
      request('/browse/featured-playlists', { params: { limit } }),
    topTracks: (limit = 20) =>
      request('/me/top/tracks', { params: { limit, time_range: 'medium_term' } }),
    topArtists: (limit = 20) =>
      request('/me/top/artists', { params: { limit, time_range: 'medium_term' } }),
    // Library feeds are fully paginated — Spotify caps each page at 50,
    // and real libraries exceed that (80 playlists / 50 albums observed
    // on this household). Each resolves to a uniform { items } so
    // SLibrary reads all three identically.
    myPlaylists: () =>
      fetchAllPages('/me/playlists', { params: { limit: 50 } }).then((items) => ({ items })),
    followedArtists: () =>
      fetchAllPages('/me/following', { params: { type: 'artist', limit: 50 }, node: (r) => r.artists })
        .then((items) => ({ items })),
    savedAlbums: () =>
      fetchAllPages('/me/albums', { params: { limit: 50 } }).then((items) => ({ items })),
    // A playlist's tracks, paginated and slimmed to {title, artist} for the
    // Spotify→Apple sync. market=AU relinks to available recordings; null /
    // local tracks (no real catalogue entry) are dropped — they'd only miss.
    // `/items` and `entry.item` since Spotify's Feb 2026 rename (the old
    // `/tracks` is 403); only owned or collaborative playlists are listable
    // in Development Mode (see music/spotify-shapes.js).
    playlistTracks: (id) =>
      fetchAllPages(`/playlists/${id}/items`, { params: { limit: 100, market: 'AU' } })
        .then((items) => items
          .map(entryTrack)
          .filter((t) => t && !t.is_local && t.name && Array.isArray(t.artists) && t.artists.length)
          .map((t) => ({ title: t.name, artist: t.artists[0]?.name || '' }))),
    // Current user's profile — used by SLibrary's "Mine first" sort to
    // tell owned playlists (owner.id === me.id) from followed ones.
    getMe: () => request('/me'),
    // Liked Songs, newest first, as {added_at, track} entries. Capped by
    // fetchAllPages at 500 (10 pages); Ben had 804 on 2026-10-31, so this is
    // the most recent 500. One open = up to 10 calls, no per-render refetch.
    likedTracks: () =>
      fetchAllPages('/me/tracks', { params: { limit: 50, market: 'AU' } }),
    // Followed podcasts as {added_at, show}, and one show's latest episodes.
    // Episodes carry resume_point (user-read-playback-position scope).
    savedShows: () => fetchAllPages('/me/shows', { params: { limit: 50 } }),
    showEpisodes: (id, limit = 5, market = 'AU') =>
      request(`/shows/${id}/episodes`, { params: { limit, market } }),
    getAlbum: (id) => request(`/albums/${id}`),
    getPlaylist: (id) => request(`/playlists/${id}`),
    getArtist: (id) => request(`/artists/${id}`),
    getArtistTopTracks: (id, market = 'AU') =>
      request(`/artists/${id}/top-tracks`, { params: { market } }),
    // Podcast endpoints. `market` is mandatory on /shows and /episodes —
    // Spotify 404s without it for shows that aren't available in every
    // territory (most). getShow returns the first page of episodes
    // inline (50 items) which is plenty for the S2 list — pagination
    // is deferred until a real "load more" need surfaces.
    getEpisode: (id, market = 'AU') => request(`/episodes/${id}`, { params: { market } }),
    getShow: (id, market = 'AU') => request(`/shows/${id}`, { params: { market } }),
  };
}

// Small async-data hook used by S0/S1/S2. Reruns when deps change;
// cancels in-flight on unmount. Returns { data, error, loading }.
export function useFetched(fetcher, deps) {
  const [state, setState] = React.useState({ data: null, error: null, loading: true });
  const [nonce, setNonce] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    setState({ data: null, error: null, loading: true });
    Promise.resolve()
      .then(() => fetcher())
      .then((data) => { if (!cancelled) setState({ data, error: null, loading: false }); })
      .catch((error) => { if (!cancelled) setState({ data: null, error, loading: false }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);
  return { ...state, refetch: React.useCallback(() => setNonce((n) => n + 1), []) };
}
